import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { ContentBlockType } from '@onesource/schemas';
import { FC, useCallback } from 'react';

import { LessonBreadcrumbs } from '#/features/community-hub/components/lesson-breadcrumbs';
import { VideoBlockPlayer } from '#/features/community-hub/components/lesson-detail-view/video-block-player';

import { useResolvedBlock } from './use-resolved-block';

type TVideoItemViewProps = {
  /** URL slug of the content block. */
  itemSlug: string;
};

/** Video content block viewer. Shared by the program-level and lesson-level routes. */
export const VideoItemView: FC<TVideoItemViewProps> = ({ itemSlug }) => {
  const {
    programId,
    moduleId,
    lessonId,
    programSlug,
    moduleSlug,
    lessonSlug,
    lesson,
    module,
    findParentLesson,
    block,
    isLoading,
    error,
  } = useResolvedBlock(itemSlug, ContentBlockType.VIMEO);

  const handleClose = useCallback(() => {
    window.history.back();
  }, []);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load content: {error.message}</Alert>
      </Box>
    );
  }

  if (!block) {
    return (
      <Box p={3}>
        <Alert severity="error">Video not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <LessonBreadcrumbs
        spaceId={programId}
        moduleId={moduleId}
        lessonId={lessonId}
        module={module}
        lesson={lesson}
        parentLesson={findParentLesson}
        currentLabel="Video"
        programSlug={programSlug}
        moduleSlug={moduleSlug}
        lessonSlug={lessonSlug}
      />

      <Box sx={{ width: { xs: '100%', md: 'calc(100% - 190px)' } }}>
        <VideoBlockPlayer block={block} lessonId={lessonId} onClose={handleClose} />
      </Box>
    </Box>
  );
};
